import { dec, QUADRI_MODELLO, type Dec, type ModelloDichiarazione, type QuadroModello } from '@praticheenergia/ad1-engine'

// A-H dal contatore che li alimenta (QUADRO_DEL_CONTATORE in packages/ad1-engine/src/quadri.ts),
// M, Q, S dai nodi del tracciato (DettaglioUsiAssoggettati, RiepilogoSaldoAccisa, DettVersamenti).
const ETICHETTE: Partial<Record<QuadroModello, string>> = {
  A: 'Energia prodotta',
  B: 'Uso promiscuo',
  C: 'Consumi esenti',
  E: 'Consumi assoggettati',
  G: 'Energia ceduta',
  H: 'Energia ricevuta',
  M: 'Usi assoggettati',
  Q: "Riepilogo dell'accisa",
  S: 'Versamenti e saldo',
}

export interface VoceRiepilogo {
  readonly quadro: QuadroModello
  readonly etichetta: string
  readonly righe: number
  readonly kwh: Dec
  readonly accisa: Dec
}

export interface Riepilogo {
  readonly voci: readonly VoceRiepilogo[]
  readonly accisa: Dec
}

const ZERO = dec(0)

/** Somme per quadro del modello calcolato: kWh e accisa, nell'ordine del tracciato. */
export function riepilogoModello(modello: ModelloDichiarazione): Riepilogo {
  const voci = QUADRI_MODELLO.flatMap((q) => {
    const righe = modello[q]
    if (!righe || righe.length === 0) return []
    const somma = (campo: 'kwh' | 'accisa') =>
      righe.reduce((t, r) => (r[campo] === undefined ? t : t.plus(dec(r[campo]))), ZERO)
    return [{ quadro: q, etichetta: ETICHETTE[q] ?? `Quadro ${q}`, righe: righe.length, kwh: somma('kwh'), accisa: somma('accisa') }]
  })
  return { voci, accisa: voci.reduce((t, v) => t.plus(v.accisa), ZERO) }
}

// 'always': in italiano il separatore delle migliaia di default manca sotto 10.000 (2000 kWh).
// Il valore e' ES2023, i tipi della lib configurata non lo conoscono ancora.
const RAGGRUPPA = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions
const KWH = new Intl.NumberFormat('it-IT', { ...RAGGRUPPA, maximumFractionDigits: 0 })
const EURO = new Intl.NumberFormat('it-IT', { ...RAGGRUPPA, style: 'currency', currency: 'EUR' })

// Solo per la vista: la conversione a number perde precisione oltre 15 cifre, irrilevante a schermo.
export const formattaKwh = (v: Dec) => `${KWH.format(Number(v.toString()))} kWh`
export const formattaEuro = (v: Dec) => EURO.format(Number(v.toString()))
